import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import { IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const ChartContainer = styled.div`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin: ${theme.spacing['2xl']} 0;

  @media (max-width: 768px) {
    margin: ${theme.spacing.xl} 0;
  }
`;

const DesktopTable = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
`;

const TableHeader = styled.th`
  padding: ${theme.spacing.lg};
  background: ${props => props.isPackageHeader ? 
    `linear-gradient(135deg, ${theme.colors.primary.main}10, ${theme.colors.primary.light}10)` : 
    'transparent'
  };
  font-weight: ${props => props.isPackageHeader ? '600' : '500'};
  color: ${theme.colors.text.primary};
  border-bottom: 1px solid ${theme.colors.secondary.dark}15;
  min-width: ${props => props.isPackageHeader ? '200px' : 'auto'};
  text-align: ${props => props.isPackageHeader ? 'center' : 'left'};

  &:first-of-type {
    padding-left: ${theme.spacing['2xl']};
  }

  &:last-child {
    padding-right: ${theme.spacing['2xl']};
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing.lg};
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.secondary.dark}15;
  text-align: ${props => props.isFeatureCell ? 'left' : 'center'};
  background: ${props => props.isHighlighted ? `${theme.colors.primary.main}05` : 'transparent'};

  &:first-of-type {
    padding-left: ${theme.spacing['2xl']};
    font-weight: 500;
    color: ${theme.colors.text.primary};
  }

  &:last-child {
    padding-right: ${theme.spacing['2xl']};
  }
`;

const MobileView = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const PackageCard = styled.div`
  border: 2px solid ${props => props.isSelected ? theme.colors.primary.main : theme.colors.secondary.dark}15;
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.lg};
  overflow: hidden;
`;

const PackageHeader = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.background.secondary};
  text-align: center;
  border-bottom: 1px solid ${theme.colors.secondary.dark}15;
`;

const PackageTitle = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const Price = styled.div`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: 600;
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.xs};
`;

const Period = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const FeatureList = styled.div`
  padding: ${theme.spacing.lg};
`;

const FeatureItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.secondary.dark}15;

  &:last-child {
    border-bottom: none;
  }
`;

const FeatureName = styled.div`
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

const FeatureValue = styled.div`
  color: ${theme.colors.text.secondary};
  text-align: right;
`;

const CheckIcon = styled.span`
  color: ${props => props.isBlue ? theme.colors.primary.main : theme.colors.primary.main};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${props => props.isBlue ? '700' : '600'};
  opacity: ${props => props.isBlue ? 1 : 0.9};
  text-shadow: ${props => props.isBlue ? `0 0 8px ${theme.colors.primary.main}30` : 'none'};
`;

const XIcon = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.xl};
`;

const AdditionalInfo = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin-top: ${theme.spacing.xs};
`;

const ExpandButton = styled(IconButton)`
  margin-left: ${theme.spacing.sm};
  color: ${theme.colors.text.secondary};

  .MuiSvgIcon-root {
    transition: transform 0.2s ease;
    transform: rotate(${props => props['data-expanded'] === 'true' ? '180deg' : '0deg'});
  }
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg} ${theme.spacing['2xl']};
  background: ${theme.colors.background.secondary};
  border-bottom: 1px solid ${theme.colors.secondary.dark}15;

  @media (max-width: 768px) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

const HeaderTitle = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.lg};
`;

const NoFeesNote = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  
  &::before {
    content: '✓';
    color: ${theme.colors.primary.main};
    font-weight: bold;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const CollapsibleContent = styled.div`
  max-height: ${props => props.isExpanded ? '5000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const features = [
  {
    name: 'Employee Limits',
    solo: '1 Employee',
    business: '5 Employees',
    businessPlus: '25 Employees',
    custom: 'Unlimited',
    additionalInfo: {
      businessPlus: 'Additional employees: $0.75',
      custom: null
    }
  },
  {
    name: 'Employee Profiles and Management',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Scheduling',
    solo: 'Unlimited',
    business: 'Unlimited',
    businessPlus: 'Unlimited',
    custom: 'Unlimited'
  },
  {
    name: 'Online Booking',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Calendar Sync',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Multi-Staff Booking',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Email Notifications',
    solo: 'Unlimited',
    business: 'Unlimited',
    businessPlus: 'Unlimited',
    custom: 'Unlimited'
  },
  {
    name: 'SMS Notifications',
    solo: 'Unlimited',
    business: 'Unlimited',
    businessPlus: 'Unlimited',
    custom: 'Unlimited'
  },
  {
    name: 'Customer Management',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Customer Profiles',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓',
    useBlueCheck: true
  },
  {
    name: 'Customer Portal',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Deposits & Tips',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Online Payments',
    solo: 'Deluxe',
    business: 'Deluxe',
    businessPlus: 'Deluxe',
    custom: 'Deluxe'
  },
  {
    name: 'Analytics & Reports',
    solo: 'Coming Soon',
    business: 'Coming Soon',
    businessPlus: 'Coming Soon',
    custom: 'Coming Soon'
  },
  {
    name: 'Branded Storefront',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Mobile Support',
    solo: '✓',
    business: '✓',
    businessPlus: '✓',
    custom: '✓'
  },
  {
    name: 'Support',
    solo: 'Ticketing System Support',
    business: 'Ticketing System Support',
    businessPlus: 'Ticketing System Support',
    custom: 'Priority Support'
  },
];

const PackageComparisonChart = ({ selectedPackage }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Listen for a global event to open the comparison chart
  useEffect(() => {
    const open = () => setIsExpanded(true);
    window.addEventListener('open-comparison-chart', open);
    return () => window.removeEventListener('open-comparison-chart', open);
  }, []);

  const renderCellContent = (value, additionalInfo, useBlueCheck) => {
    if (value === '✓') {
      return <CheckIcon isBlue={useBlueCheck}>✓</CheckIcon>;
    } else if (value === '✕') {
      return <XIcon>✕</XIcon>;
    } else {
      return (
        <>
          {value}
          {additionalInfo && <AdditionalInfo>{additionalInfo}</AdditionalInfo>}
        </>
      );
    }
  };

  const packages = [
    {
      id: 'solo',
      name: 'Solo',
      price: '20',
      isSelected: selectedPackage === 'solo'
    },
    {
      id: 'business',
      name: 'Business',
      price: '40',
      isSelected: selectedPackage === 'business'
    },
    {
      id: 'business_plus',
      name: 'Business Plus',
      price: '80',
      isSelected: selectedPackage === 'business_plus'
    },
    {
      id: 'custom',
      name: 'Custom',
      price: '$???',
      isContactOnly: true,
      isSelected: selectedPackage === 'custom' || selectedPackage === 'enterprise',
    },
  ];

  const getFeatureColumnKey = (packageId) => {
    if (packageId === 'solo') return 'solo';
    if (packageId === 'business') return 'business';
    if (packageId === 'business_plus') return 'businessPlus';
    if (packageId === 'enterprise') return 'custom';
    return 'custom';
  };

  const isCustomSelected = selectedPackage === 'custom' || selectedPackage === 'enterprise';

  return (
    <ChartContainer>
      <ChartHeader>
        <HeaderTitle>Package Comparison</HeaderTitle>
        <NoFeesNote>No hidden fees - flat monthly rates</NoFeesNote>
        <ExpandButton size="small" data-expanded={isExpanded.toString()} onClick={toggleExpand} style={{ display: 'none' }}>
          <KeyboardArrowDownIcon />
        </ExpandButton>
      </ChartHeader>

      <CollapsibleContent isExpanded={isExpanded}>
        <DesktopTable>
          <Table>
            <thead>
              <tr>
                <TableHeader>Features</TableHeader>
                <TableHeader isPackageHeader>
                  <div>Solo</div>
                  <Price>$20</Price>
                  <Period>/month</Period>
                </TableHeader>
                <TableHeader isPackageHeader>
                  <div>Business</div>
                  <Price>$40</Price>
                  <Period>/month</Period>
                </TableHeader>
                <TableHeader isPackageHeader>
                  <div>Business Plus</div>
                  <Price>$80</Price>
                  <Period>/month</Period>
                </TableHeader>
                <TableHeader isPackageHeader>
                  <div>Custom</div>
                  <Price>$???</Price>
                  <Period>/month</Period>
                </TableHeader>
              </tr>
            </thead>
            <tbody>
              {features.map((feature) => (
                <tr key={feature.name}>
                  <TableCell isFeatureCell>{feature.name}</TableCell>
                  <TableCell isHighlighted={selectedPackage === 'solo'}>
                    {renderCellContent(
                      feature.solo,
                      feature.additionalInfo?.solo,
                      feature.useBlueCheck
                    )}
                  </TableCell>
                  <TableCell isHighlighted={selectedPackage === 'business'}>
                    {renderCellContent(
                      feature.business,
                      feature.additionalInfo?.business,
                      feature.useBlueCheck
                    )}
                  </TableCell>
                  <TableCell isHighlighted={selectedPackage === 'business_plus'}>
                    {renderCellContent(
                      feature.businessPlus,
                      feature.additionalInfo?.businessPlus,
                      feature.useBlueCheck
                    )}
                  </TableCell>
                  <TableCell isHighlighted={isCustomSelected}>
                    {renderCellContent(
                      feature.custom,
                      feature.additionalInfo?.custom,
                      feature.useBlueCheck
                    )}
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </Table>
        </DesktopTable>

        <MobileView>
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} isSelected={pkg.isSelected}>
              <PackageHeader>
                <PackageTitle>{pkg.name}</PackageTitle>
                <Price>{pkg.isContactOnly ? '$???' : `$${pkg.price}`}</Price>
                <Period>/month</Period>
              </PackageHeader>
              <FeatureList>
                {features.map((feature) => (
                  <FeatureItem key={feature.name}>
                    <FeatureName>{feature.name}</FeatureName>
                    <FeatureValue>
                      {renderCellContent(
                        feature[getFeatureColumnKey(pkg.id)],
                        feature.additionalInfo?.[getFeatureColumnKey(pkg.id)],
                        feature.useBlueCheck
                      )}
                    </FeatureValue>
                  </FeatureItem>
                ))}
              </FeatureList>
            </PackageCard>
          ))}
        </MobileView>
      </CollapsibleContent>
    </ChartContainer>
  );
};

export default PackageComparisonChart; 